/**
 * Analytics Query Functions
 * Database queries for fetching analytics data from Supabase
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@limo/database'
import type {
  DateRange,
  DateRangePreset,
  OverviewStats,
  TrafficDataPoint,
  RevenueDataPoint,
  TopPage,
  BookingFunnelData,
  FunnelStep,
  AdPerformanceData,
  DeviceBreakdown,
} from './types'

type TypedSupabaseClient = SupabaseClient<Database>

/**
 * Get date range from preset
 */
export function getDateRangeFromPreset(preset: DateRangePreset): DateRange {
  const end = new Date()
  end.setHours(23, 59, 59, 999)

  const start = new Date()
  start.setHours(0, 0, 0, 0)

  switch (preset) {
    case '7d':
      start.setDate(start.getDate() - 6)
      break
    case '14d':
      start.setDate(start.getDate() - 13)
      break
    case '30d':
      start.setDate(start.getDate() - 29)
      break
    case '90d':
      start.setDate(start.getDate() - 89)
      break
  }

  return { start, end }
}

/**
 * Format date as YYYY-MM-DD for database queries
 */
function formatDate(date: Date): string {
  return date.toISOString().split('T')[0]
}

/**
 * Get previous period date range for comparison
 */
function getPreviousPeriod(dateRange: DateRange): DateRange {
  const duration = dateRange.end.getTime() - dateRange.start.getTime()
  const previousEnd = new Date(dateRange.start.getTime() - 1)
  previousEnd.setHours(23, 59, 59, 999)
  const previousStart = new Date(previousEnd.getTime() - duration)
  previousStart.setHours(0, 0, 0, 0)
  return { start: previousStart, end: previousEnd }
}

/**
 * Calculate percentage change between two values
 */
function calculateChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0
  return Math.round(((current - previous) / previous) * 100 * 10) / 10
}

/**
 * Get overview stats with comparison to previous period
 */
export async function getOverviewStats(
  supabase: TypedSupabaseClient,
  accountId: string,
  dateRange: DateRange
): Promise<OverviewStats> {
  const startDate = formatDate(dateRange.start)
  const endDate = formatDate(dateRange.end)

  // Get aggregated stats from analytics_daily_stats
  const { data: currentStats, error: currentError } = await supabase
    .from('analytics_daily_stats')
    .select(
      'pageviews, unique_visitors, sessions, bookings_completed, total_revenue, avg_session_duration_seconds, bounce_rate'
    )
    .eq('account_id', accountId)
    .gte('date', startDate)
    .lte('date', endDate)

  if (currentError) throw currentError

  // Aggregate current period stats
  const current = {
    pageviews: currentStats?.reduce((sum, row) => sum + row.pageviews, 0) || 0,
    uniqueVisitors:
      currentStats?.reduce((sum, row) => sum + row.unique_visitors, 0) || 0,
    sessions: currentStats?.reduce((sum, row) => sum + row.sessions, 0) || 0,
    bookings:
      currentStats?.reduce((sum, row) => sum + row.bookings_completed, 0) || 0,
    revenue:
      currentStats?.reduce(
        (sum, row) => sum + (row.total_revenue || 0),
        0
      ) || 0,
    avgSessionDuration:
      currentStats && currentStats.length > 0
        ? currentStats.reduce(
            (sum, row) => sum + (row.avg_session_duration_seconds || 0),
            0
          ) / currentStats.length
        : 0,
    bounceRate:
      currentStats && currentStats.length > 0
        ? currentStats.reduce(
            (sum, row) => sum + (row.bounce_rate || 0),
            0
          ) / currentStats.length
        : 0,
  }

  // Get previous period stats for comparison
  const previousPeriod = getPreviousPeriod(dateRange)
  const previousStartDate = formatDate(previousPeriod.start)
  const previousEndDate = formatDate(previousPeriod.end)

  const { data: previousStats } = await supabase
    .from('analytics_daily_stats')
    .select('pageviews, unique_visitors, sessions, bookings_completed, total_revenue')
    .eq('account_id', accountId)
    .gte('date', previousStartDate)
    .lte('date', previousEndDate)

  const previous = {
    pageviews: previousStats?.reduce((sum, row) => sum + row.pageviews, 0) || 0,
    uniqueVisitors:
      previousStats?.reduce((sum, row) => sum + row.unique_visitors, 0) || 0,
    sessions: previousStats?.reduce((sum, row) => sum + row.sessions, 0) || 0,
    bookings:
      previousStats?.reduce((sum, row) => sum + row.bookings_completed, 0) || 0,
    revenue:
      previousStats?.reduce(
        (sum, row) => sum + (row.total_revenue || 0),
        0
      ) || 0,
  }

  return {
    pageviews: current.pageviews,
    uniqueVisitors: current.uniqueVisitors,
    sessions: current.sessions,
    bookings: current.bookings,
    revenue: current.revenue,
    avgSessionDuration: Math.round(current.avgSessionDuration),
    bounceRate: Math.round(current.bounceRate * 10) / 10,
    pageviewsChange: calculateChange(current.pageviews, previous.pageviews),
    visitorsChange: calculateChange(
      current.uniqueVisitors,
      previous.uniqueVisitors
    ),
    sessionsChange: calculateChange(current.sessions, previous.sessions),
    bookingsChange: calculateChange(current.bookings, previous.bookings),
    revenueChange: calculateChange(current.revenue, previous.revenue),
  }
}

/**
 * Get daily traffic data for charts
 */
export async function getDailyTraffic(
  supabase: TypedSupabaseClient,
  accountId: string,
  dateRange: DateRange
): Promise<TrafficDataPoint[]> {
  const startDate = formatDate(dateRange.start)
  const endDate = formatDate(dateRange.end)

  const { data, error } = await supabase
    .from('analytics_daily_stats')
    .select('date, pageviews, unique_visitors, sessions')
    .eq('account_id', accountId)
    .gte('date', startDate)
    .lte('date', endDate)
    .order('date', { ascending: true })

  if (error) throw error

  // Create a map of existing data
  const dataMap = new Map(
    (data || []).map((row) => [
      row.date,
      {
        date: row.date,
        pageviews: row.pageviews,
        uniqueVisitors: row.unique_visitors,
        sessions: row.sessions,
      },
    ])
  )

  // Fill in missing dates with zeros
  const result: TrafficDataPoint[] = []
  const current = new Date(dateRange.start)
  while (current <= dateRange.end) {
    const dateStr = formatDate(current)
    result.push(
      dataMap.get(dateStr) || {
        date: dateStr,
        pageviews: 0,
        uniqueVisitors: 0,
        sessions: 0,
      }
    )
    current.setDate(current.getDate() + 1)
  }

  return result
}

/**
 * Get daily revenue data for charts
 */
export async function getDailyRevenue(
  supabase: TypedSupabaseClient,
  accountId: string,
  dateRange: DateRange
): Promise<RevenueDataPoint[]> {
  const startDate = formatDate(dateRange.start)
  const endDate = formatDate(dateRange.end)

  const { data, error } = await supabase
    .from('analytics_daily_stats')
    .select('date, total_revenue, booking_count')
    .eq('account_id', accountId)
    .gte('date', startDate)
    .lte('date', endDate)
    .order('date', { ascending: true })

  if (error) throw error

  // Create a map of existing data
  const dataMap = new Map(
    (data || []).map((row) => [
      row.date,
      {
        date: row.date,
        revenue: row.total_revenue || 0,
        bookings: row.booking_count,
      },
    ])
  )

  // Fill in missing dates with zeros
  const result: RevenueDataPoint[] = []
  const current = new Date(dateRange.start)
  while (current <= dateRange.end) {
    const dateStr = formatDate(current)
    result.push(
      dataMap.get(dateStr) || {
        date: dateStr,
        revenue: 0,
        bookings: 0,
      }
    )
    current.setDate(current.getDate() + 1)
  }

  return result
}

/**
 * Get top pages by pageviews
 */
export async function getTopPages(
  supabase: TypedSupabaseClient,
  accountId: string,
  dateRange: DateRange,
  limit: number = 10
): Promise<TopPage[]> {
  const startDate = formatDate(dateRange.start)
  const endDate = formatDate(dateRange.end)

  // Query raw events for page data (aggregate on client side)
  const { data, error } = await supabase
    .from('analytics_events')
    .select('page_path, visitor_id, session_id')
    .eq('account_id', accountId)
    .eq('event_type', 'pageview')
    .gte('timestamp', `${startDate}T00:00:00Z`)
    .lte('timestamp', `${endDate}T23:59:59Z`)

  if (error) throw error

  // Aggregate by page path
  const pageStats = new Map<
    string,
    {
      pageviews: number
      visitors: Set<string>
      sessions: Set<string>
      singlePageSessions: Set<string>
    }
  >()

  // Track session page counts for bounce rate
  const sessionPages = new Map<string, Set<string>>()

  for (const row of data || []) {
    if (!row.page_path) continue

    // Track pages per session
    if (!sessionPages.has(row.session_id)) {
      sessionPages.set(row.session_id, new Set())
    }
    sessionPages.get(row.session_id)!.add(row.page_path)

    // Track page stats
    if (!pageStats.has(row.page_path)) {
      pageStats.set(row.page_path, {
        pageviews: 0,
        visitors: new Set(),
        sessions: new Set(),
        singlePageSessions: new Set(),
      })
    }
    const stats = pageStats.get(row.page_path)!
    stats.pageviews++
    stats.visitors.add(row.visitor_id)
    stats.sessions.add(row.session_id)
  }

  // Calculate bounce rate for each page (sessions with only that page)
  for (const [sessionId, pages] of sessionPages) {
    if (pages.size === 1) {
      const page = Array.from(pages)[0]
      const stats = pageStats.get(page)
      if (stats) {
        stats.singlePageSessions.add(sessionId)
      }
    }
  }

  // Convert to array and sort by pageviews
  const result: TopPage[] = Array.from(pageStats.entries())
    .map(([path, stats]) => ({
      path,
      pageviews: stats.pageviews,
      uniqueVisitors: stats.visitors.size,
      bounceRate:
        stats.sessions.size > 0
          ? Math.round(
              (stats.singlePageSessions.size / stats.sessions.size) * 100
            )
          : 0,
    }))
    .sort((a, b) => b.pageviews - a.pageviews)
    .slice(0, limit)

  return result
}

/**
 * Get booking funnel data
 */
export async function getBookingFunnel(
  supabase: TypedSupabaseClient,
  accountId: string,
  dateRange: DateRange
): Promise<BookingFunnelData> {
  const startDate = formatDate(dateRange.start)
  const endDate = formatDate(dateRange.end)

  // Get counts for each funnel step
  const { data: funnelData, error } = await supabase
    .from('analytics_daily_stats')
    .select(
      'unique_visitors, bookings_started, vehicles_selected, payments_started, bookings_completed'
    )
    .eq('account_id', accountId)
    .gte('date', startDate)
    .lte('date', endDate)

  if (error) throw error

  // Sum up counts across days
  const totals = {
    visitors: funnelData?.reduce((sum, row) => sum + row.unique_visitors, 0) || 0,
    started: funnelData?.reduce((sum, row) => sum + row.bookings_started, 0) || 0,
    vehicles:
      funnelData?.reduce((sum, row) => sum + row.vehicles_selected, 0) || 0,
    payments:
      funnelData?.reduce((sum, row) => sum + row.payments_started, 0) || 0,
    completed:
      funnelData?.reduce((sum, row) => sum + row.bookings_completed, 0) || 0,
  }

  // Build funnel steps
  const steps: FunnelStep[] = [
    {
      name: 'Visitors',
      event: 'pageview',
      count: totals.visitors,
      conversionRate: 100,
      dropoffRate: 0,
    },
    {
      name: 'Started Booking',
      event: 'booking_started',
      count: totals.started,
      conversionRate:
        totals.visitors > 0
          ? Math.round((totals.started / totals.visitors) * 1000) / 10
          : 0,
      dropoffRate:
        totals.visitors > 0
          ? Math.round(
              ((totals.visitors - totals.started) / totals.visitors) * 1000
            ) / 10
          : 0,
    },
    {
      name: 'Selected Vehicle',
      event: 'vehicle_selected',
      count: totals.vehicles,
      conversionRate:
        totals.visitors > 0
          ? Math.round((totals.vehicles / totals.visitors) * 1000) / 10
          : 0,
      dropoffRate:
        totals.started > 0
          ? Math.round(
              ((totals.started - totals.vehicles) / totals.started) * 1000
            ) / 10
          : 0,
    },
    {
      name: 'Payment Started',
      event: 'payment_started',
      count: totals.payments,
      conversionRate:
        totals.visitors > 0
          ? Math.round((totals.payments / totals.visitors) * 1000) / 10
          : 0,
      dropoffRate:
        totals.vehicles > 0
          ? Math.round(
              ((totals.vehicles - totals.payments) / totals.vehicles) * 1000
            ) / 10
          : 0,
    },
    {
      name: 'Completed',
      event: 'booking_completed',
      count: totals.completed,
      conversionRate:
        totals.visitors > 0
          ? Math.round((totals.completed / totals.visitors) * 1000) / 10
          : 0,
      dropoffRate:
        totals.payments > 0
          ? Math.round(
              ((totals.payments - totals.completed) / totals.payments) * 1000
            ) / 10
          : 0,
    },
  ]

  return {
    steps,
    totalStarted: totals.started,
    totalCompleted: totals.completed,
    overallConversionRate:
      totals.visitors > 0
        ? Math.round((totals.completed / totals.visitors) * 1000) / 10
        : 0,
  }
}

/**
 * Get ad performance data (UTM attribution)
 */
export async function getAdPerformance(
  supabase: TypedSupabaseClient,
  accountId: string,
  dateRange: DateRange
): Promise<AdPerformanceData[]> {
  const startDate = formatDate(dateRange.start)
  const endDate = formatDate(dateRange.end)

  const { data, error } = await supabase
    .from('analytics_utm_daily_stats')
    .select('utm_source, utm_medium, utm_campaign, visitors, sessions, bookings_completed, revenue')
    .eq('account_id', accountId)
    .gte('date', startDate)
    .lte('date', endDate)

  if (error) throw error

  // Aggregate by UTM combination
  const utmStats = new Map<
    string,
    {
      utm_source: string
      utm_medium: string | null
      utm_campaign: string | null
      visitors: number
      sessions: number
      bookings: number
      revenue: number
    }
  >()

  for (const row of data || []) {
    const key = `${row.utm_source}|${row.utm_medium || ''}|${row.utm_campaign || ''}`

    if (!utmStats.has(key)) {
      utmStats.set(key, {
        utm_source: row.utm_source,
        utm_medium: row.utm_medium,
        utm_campaign: row.utm_campaign,
        visitors: 0,
        sessions: 0,
        bookings: 0,
        revenue: 0,
      })
    }

    const stats = utmStats.get(key)!
    stats.visitors += row.visitors
    stats.sessions += row.sessions
    stats.bookings += row.bookings_completed
    stats.revenue += row.revenue || 0
  }

  // Convert to array with conversion rate and sort by revenue
  return Array.from(utmStats.values())
    .map((stats) => ({
      ...stats,
      conversionRate:
        stats.visitors > 0
          ? Math.round((stats.bookings / stats.visitors) * 1000) / 10
          : 0,
    }))
    .sort((a, b) => b.revenue - a.revenue)
}

/**
 * Get device breakdown
 */
export async function getDeviceBreakdown(
  supabase: TypedSupabaseClient,
  accountId: string,
  dateRange: DateRange
): Promise<DeviceBreakdown> {
  const startDate = formatDate(dateRange.start)
  const endDate = formatDate(dateRange.end)

  const { data, error } = await supabase
    .from('analytics_events')
    .select('device_type')
    .eq('account_id', accountId)
    .eq('event_type', 'pageview')
    .gte('timestamp', `${startDate}T00:00:00Z`)
    .lte('timestamp', `${endDate}T23:59:59Z`)

  if (error) throw error

  const counts = {
    desktop: 0,
    mobile: 0,
    tablet: 0,
  }

  for (const row of data || []) {
    if (row.device_type === 'desktop') counts.desktop++
    else if (row.device_type === 'mobile') counts.mobile++
    else if (row.device_type === 'tablet') counts.tablet++
    else counts.desktop++ // Default to desktop for unknown
  }

  const total = counts.desktop + counts.mobile + counts.tablet

  return {
    desktop: total > 0 ? Math.round((counts.desktop / total) * 100) : 0,
    mobile: total > 0 ? Math.round((counts.mobile / total) * 100) : 0,
    tablet: total > 0 ? Math.round((counts.tablet / total) * 100) : 0,
  }
}
